import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Container,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from '@mui/material';
import axios from '../../../api/axios';
import { AuthContext } from '../../../contexts/AuthContext';
import ExerciseHeader from '../../../components/Exercise/ExerciseHeader';
import ExerciseContent from '../../../components/Exercise/ExerciseContent';
import ExerciseNavigation from '../../../components/Exercise/ExerciseNavigation';
import AIChatSidebar from '../../../components/Exercise/AIChatSidebar';
import EditDialog from '../../../components/Exercise/EditDialog';
import NotebookCodeQuestion from '../../../components/Exercise/NotebookCodeQuestion';

function NotebookExerciseDetail() {
  const { exampleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  
  const [example, setExample] = useState(null);
  const [notebook, setNotebook] = useState(null);
  const [sectionExamples, setSectionExamples] = useState([]);
  const [sectionIndex, setSectionIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [studentAnswer, setStudentAnswer] = useState(null);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingExample, setEditingExample] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveSectionTitles, setMoveSectionTitles] = useState([]);
  const [moveTargetSectionTitle, setMoveTargetSectionTitle] = useState('');
  const [moveSubmitting, setMoveSubmitting] = useState(false);

  useEffect(() => {
    // 切换题目时重置AI助手状态
    setAiChatOpen(false);
    fetchExample(location.state);
    checkBookmarkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exampleId, location.state]);

  const fetchExample = async (navState) => {
    try {
      setLoading(true);
      const response = await axios.get(`/notebook-examples/${exampleId}`);
      setExample(response.data);
      setNotebook(response.data.notebook);
      
      // Fetch student's answer
      try {
        const questionId = `notebook_${exampleId}`;
        const submissionsRes = await axios.get(`/submissions/question/${questionId}`);
        const submissions = submissionsRes.data || [];
        if (submissions.length > 0) {
          const latestSubmission = submissions[0];
          try {
            const answerData = JSON.parse(latestSubmission.code);
            setStudentAnswer(answerData.answer);
          } catch (e) {
            setStudentAnswer(latestSubmission.code);
          }
        }
      } catch (error) {
        console.error('Error fetching student answer:', error);
      }
      
      // 若从「按难度筛选」或「按题目类型」进入，用 state 的 list 作为上一题/下一题列表，不按章节拉取
      if ((navState?.fromView === 'difficulty' || navState?.fromView === 'type') && Array.isArray(navState?.list) && navState.list.length > 0) {
        const list = navState.list;
        const sectionExs = list.map((q) => ({ id: parseInt(String(q.id).replace('notebook_', ''), 10) }));
        const sectionIdx = list.findIndex((q) => String(q.id).replace('notebook_', '') === String(exampleId));
        setSectionExamples(sectionExs);
        setSectionIndex(sectionIdx >= 0 ? sectionIdx : 0);
        setLoading(false);
        return;
      }
      
      // Fetch all examples from the same notebook
      if (response.data.notebook) {
        const allResponse = await axios.get(`/notebook-examples/notebook/${response.data.notebook.notebookId}`);
        const allExs = allResponse.data.examples;
        
        // Filter examples from the same section
        const currentSectionTitle = response.data.sectionTitle;
        let sectionExs = allExs.filter(ex => ex.sectionTitle === currentSectionTitle);
        
        // Sort section examples to match the order in question list
        sectionExs.sort((a, b) => {
          if (a.conceptBlockIndex != null && b.conceptBlockIndex != null) {
            if (a.conceptBlockIndex !== b.conceptBlockIndex) {
              return a.conceptBlockIndex - b.conceptBlockIndex;
            }
          }
          if (a.exampleIndex != null && b.exampleIndex != null) {
            if (a.exampleIndex !== b.exampleIndex) {
              return a.exampleIndex - b.exampleIndex;
            }
          }
          return a.id - b.id;
        });
        
        setSectionExamples(sectionExs);
        
        const sectionIdx = sectionExs.findIndex(ex => ex.id === parseInt(exampleId));
        setSectionIndex(sectionIdx);
      }
    } catch (error) {
      console.error('Error fetching example:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // 返回到题目列表页面
    // 如果有 notebookId，返回到该笔记本的题目列表，否则返回到通用题目列表
    if (notebook?.notebookId) {
      navigate(`/questions/notebook/${notebook.notebookId}`);
    } else {
      navigate('/questions');
    }
  };

  const handlePrevious = () => {
    if (sectionIndex > 0 && sectionExamples.length > 0) {
      const prevExample = sectionExamples[sectionIndex - 1];
      navigate(`/notebook-exercise/${prevExample.id}`, { state: location.state });
    }
  };

  const handleNext = () => {
    if (sectionIndex < sectionExamples.length - 1 && sectionExamples.length > 0) {
      const nextExample = sectionExamples[sectionIndex + 1];
      navigate(`/notebook-exercise/${nextExample.id}`, { state: location.state });
    }
  };

  const handleAnswerSubmitted = (answer) => {
    setStudentAnswer(answer);
  };

  const handleDelete = async () => {
    if (!window.confirm('确定要删除这道练习题吗？删除后将无法恢复。')) {
      return;
    }

    try {
      // Determine which question to navigate to after deletion
      let nextExampleId = null;
      
      // Try to navigate to next question first
      if (sectionIndex < sectionExamples.length - 1 && sectionExamples.length > 0) {
        const nextExample = sectionExamples[sectionIndex + 1];
        nextExampleId = nextExample.id;
      } 
      // If no next question, try previous
      else if (sectionIndex > 0 && sectionExamples.length > 0) {
        const prevExample = sectionExamples[sectionIndex - 1];
        nextExampleId = prevExample.id;
      }
      
      await axios.delete(`/notebook-examples/${exampleId}`);
      alert('练习题删除成功！');
      
      // Navigate to next/previous question (保留按难度/按题目类型进入的 state), or dashboard if no other questions
      if (nextExampleId) {
        navigate(`/notebook-exercise/${nextExampleId}`, { state: location.state });
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error deleting notebook example:', error);
      alert('删除失败：' + (error.response?.data?.error || '请重试'));
    }
  };

  const handleEdit = () => {
    setEditingExample({ ...example });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingExample) return;
    
    setSaving(true);
    try {
      const updateData = {};
      
      if (editingExample.question !== example.question) {
        updateData.question = editingExample.question;
      }
      if (editingExample.explanation !== example.explanation) {
        updateData.explanation = editingExample.explanation;
      }
      
      // Type-specific fields
      if (example.questionType === 'multiple_choice') {
        if (JSON.stringify(editingExample.options) !== JSON.stringify(example.options)) {
          updateData.options = editingExample.options;
        }
        if (editingExample.correctAnswer !== example.correctAnswer) {
          updateData.correctAnswer = editingExample.correctAnswer;
        }
      } else if (example.questionType === 'short_answer') {
        if (editingExample.answer !== example.answer) {
          updateData.answer = editingExample.answer;
        }
      } else if (example.questionType === 'proof') {
        if (editingExample.proof !== example.proof) {
          updateData.proof = editingExample.proof;
        }
      } else if (example.questionType === 'code') {
        if (editingExample.codeAnswer !== example.codeAnswer) {
          updateData.codeAnswer = editingExample.codeAnswer;
        }
        if (editingExample.publicTests !== example.publicTests) {
          updateData.publicTests = editingExample.publicTests;
        }
        if (editingExample.secretTests !== example.secretTests) {
          updateData.secretTests = editingExample.secretTests;
        }
      } else if (example.questionType === 'code_tracing') {
        if (editingExample.codeAnswer !== example.codeAnswer) {
          updateData.codeAnswer = editingExample.codeAnswer;
        }
        if (JSON.stringify(editingExample.options) !== JSON.stringify(example.options)) {
          updateData.options = editingExample.options;
        }
        if (editingExample.correctAnswer !== example.correctAnswer) {
          updateData.correctAnswer = editingExample.correctAnswer;
        }
        if (editingExample.answer !== example.answer) {
          updateData.answer = editingExample.answer;
        }
      }
      
      if (Object.keys(updateData).length === 0) {
        setEditDialogOpen(false);
        return;
      }
      
      const response = await axios.put(`/notebook-examples/${exampleId}`, updateData);
      setExample(response.data.example);
      alert('题目更新成功！');
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating example:', error);
      alert('更新失败：' + (error.response?.data?.error || '请重试'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingExample(null);
  };

  const handleOpenMoveDialog = async () => {
    setMoveTargetSectionTitle(example?.sectionTitle || '');
    setMoveSubmitting(false);
    if (notebook?.notebookId) {
      try {
        const res = await axios.get(`/notes/${notebook.notebookId}`);
        const outlines = res.data?.outline?.outlines;
        const titles = outlines && typeof outlines === 'object'
          ? Object.keys(outlines).filter((k) => typeof outlines[k] === 'string')
          : [];
        setMoveSectionTitles(titles.length > 0 ? titles : (example?.sectionTitle ? [example.sectionTitle] : []));
      } catch (e) {
        setMoveSectionTitles(example?.sectionTitle ? [example.sectionTitle] : []);
      }
    } else {
      setMoveSectionTitles(example?.sectionTitle ? [example.sectionTitle] : []);
    }
    setMoveDialogOpen(true);
  };

  const handleCloseMoveDialog = () => {
    if (!moveSubmitting) {
      setMoveDialogOpen(false);
      setMoveTargetSectionTitle('');
    }
  };

  const handleConfirmMove = async () => {
    if (!moveTargetSectionTitle || !example) return;
    setMoveSubmitting(true);
    try {
      await axios.put(`/notebook-examples/${exampleId}`, { sectionTitle: moveTargetSectionTitle });
      setExample((prev) => (prev ? { ...prev, sectionTitle: moveTargetSectionTitle } : null));
      setMoveDialogOpen(false);
      setMoveTargetSectionTitle('');
      alert('已移动到该章节');
      fetchExample(location.state);
    } catch (err) {
      console.warn('Move example failed:', err);
      alert(err?.response?.data?.error || '移动失败，请重试');
    } finally {
      setMoveSubmitting(false);
    }
  };

  const checkBookmarkStatus = async () => {
    try {
      const response = await axios.get(`/bookmarks/${exampleId}/status`);
      setIsBookmarked(response.data.isBookmarked);
    } catch (error) {
      console.error('Error checking bookmark status:', error);
    }
  };

  const handleBookmark = async () => {
    if (bookmarkLoading) return;
    
    setBookmarkLoading(true);
    try {
      if (isBookmarked) {
        // Remove from bookmarks
        await axios.delete(`/bookmarks/${exampleId}`);
        setIsBookmarked(false);
      } else {
        // Add to bookmarks
        await axios.post(`/bookmarks/${exampleId}`);
        setIsBookmarked(true);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      const errorMessage = error.response?.data?.error || '操作失败，请重试';
      alert(errorMessage);
    } finally {
      setBookmarkLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!example) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <ExerciseHeader
          notebook={null}
          example={null}
          sectionExamples={[]}
          sectionIndex={0}
          aiChatOpen={false}
          onBack={handleBack}
          onToggleAI={() => {}}
          onEdit={() => {}}
          onDelete={() => {}}
          user={user}
        />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">练习题不存在</Alert>
        </Container>
      </Box>
    );
  }

  // For code questions, use the same UI as QuestionDetail
  if (example?.questionType === 'code') {
    return (
      <>
        <NotebookCodeQuestion 
          example={example}
          onUpdate={fetchExample}
          sectionExamples={sectionExamples}
          sectionIndex={sectionIndex}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onDelete={handleDelete}
          onMoveToSection={user?.role === 'teacher' ? handleOpenMoveDialog : undefined}
        />
        <EditDialog
          open={editDialogOpen}
          example={example}
          editingExample={editingExample}
          setEditingExample={setEditingExample}
          saving={saving}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
        />
      </>
    );
  }

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      bgcolor: 'background.default',
      overflow: 'hidden'
    }}>
      <ExerciseHeader
        notebook={notebook}
        example={example}
        sectionExamples={sectionExamples}
        sectionIndex={sectionIndex}
        aiChatOpen={aiChatOpen}
        onBack={handleBack}
        onToggleAI={() => setAiChatOpen(!aiChatOpen)}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onMoveToSection={user?.role === 'teacher' ? handleOpenMoveDialog : undefined}
        onBookmark={handleBookmark}
        isBookmarked={isBookmarked}
        user={user}
      />

      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <ExerciseContent 
          example={example} 
          onAnswerSubmitted={handleAnswerSubmitted} 
        />
        
        <AIChatSidebar
          open={aiChatOpen}
          exampleId={exampleId}
          question={example?.question}
          questionType={example?.questionType}
          studentAnswer={studentAnswer}
          onClose={() => setAiChatOpen(false)}
        />
      </Box>

      <ExerciseNavigation
        sectionExamples={sectionExamples}
        sectionIndex={sectionIndex}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />

      <EditDialog
        open={editDialogOpen}
        example={example}
        editingExample={editingExample}
        setEditingExample={setEditingExample}
        saving={saving}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
      />

      <Dialog open={moveDialogOpen} onClose={handleCloseMoveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>移动到章节</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="move-section-label">目标章节</InputLabel>
            <Select
              labelId="move-section-label"
              value={moveTargetSectionTitle}
              label="目标章节"
              onChange={(e) => setMoveTargetSectionTitle(e.target.value)}
            >
              {moveSectionTitles.map((st) => (
                <MenuItem key={st} value={st}>{st}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMoveDialog} disabled={moveSubmitting}>取消</Button>
          <Button variant="contained" onClick={handleConfirmMove} disabled={moveSubmitting || !moveTargetSectionTitle}>
            {moveSubmitting ? '移动中…' : '确定'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NotebookExerciseDetail;
